import { useEffect, useState } from "react";
import { motion } from "@/lib/motion";
import { Reveal, Overline } from "./Reveal";
import { getHomepageApproach } from "../lib/sanityQueries";
import { peek } from "../lib/sanity";

const FALLBACK_CHAPTERS = [
    {
        label: "01",
        title: "Understand the business",
        description: "We start with your operations, risk, and growth goals — because technology decisions are business decisions.",
    },
    {
        label: "02",
        title: "Secure the foundation",
        description: "We close hidden security, access, and operational gaps first — because prevention is always better than recovery.",
    },
    {
        label: "03",
        title: "Structure for clarity",
        description: "We document and standardize onboarding, access, and governance — replacing inconsistent processes with operational maturity.",
    },
    {
        label: "04",
        title: "Scale with confidence",
        description: "We provide ongoing strategic guidance so technology supports your growth rather than becoming a bottleneck.",
    },
];

export const Approach = () => {
    const [approach, setApproach] = useState(() => peek(getHomepageApproach));

    useEffect(() => {
        let active = true;
        getHomepageApproach()
            .then((data) => {
                if (active && data) setApproach(data);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, []);

    const chapters =
        approach?.approachChapters?.length
            ? approach.approachChapters
            : FALLBACK_CHAPTERS;

    return (
    <section
        id="approach"
        data-testid="approach-section"
        className="relative border-y border-brand-line el-brand-fill py-28 sm:py-36"
    >
        <div className="pointer-events-none absolute inset-0 el-noise" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
            <div className="mb-16 max-w-2xl">
                <Reveal>
                    <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-brand-accent">
                        Our Approach
                    </span>
                </Reveal>
                <Reveal delay={0.05}>
                    <h2 className="mt-5 font-heading text-4xl font-extrabold leading-[1.02] tracking-tight sm:text-5xl">
                        {approach?.approachHeadline ||
                            "A deliberate path to secure, structured, and scalable."}
                    </h2>
                </Reveal>
            </div>

            <div className="divide-y divide-[color:var(--el-brand-fill-divider)] border-t border-[color:var(--el-brand-fill-divider)]">
                {chapters.map((c, i) => (
                    <Reveal key={c._key || c.label} delay={i * 0.05}>
                        <motion.div
                            whileHover={{ x: 8 }}
                            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                            className="group grid grid-cols-1 gap-6 py-10 sm:grid-cols-12 sm:items-baseline"
                        >
                            <div className="sm:col-span-2">
                                <span className="font-heading text-5xl font-extrabold tracking-tight text-brand-accent sm:text-6xl">
                                    {c.label}
                                </span>
                            </div>
                            <h3 className="font-heading text-2xl font-bold tracking-tight sm:col-span-4 sm:text-3xl">
                                {c.title}
                            </h3>
                            <p className="max-w-xl text-sm leading-relaxed opacity-80 sm:col-span-6 sm:text-base">
                                {c.description}
                            </p>
                        </motion.div>
                    </Reveal>
                ))}
            </div>
        </div>
    </section>
);
};
